import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "store";
import {
  fetchOrders,
  triggerShipstationManualPull,
} from "services/api/integrations";
import {
  fetchIntegrationStoresThunk,
  fetchShipmentsThunk,
} from "store/shipmentsSlice/thunks/fetchShipmentsThunk.thunk";
import { Portal } from "react-portal";
import { usePopper } from "react-popper";
import { FaChevronDown, FaSyncAlt, FaTimes } from "react-icons/fa";
import { Button } from "components/atoms/Button";
import { Logo } from "components/atoms/LogoCircle";
import { useTheme } from "styled-components";
import { AnimatePresence, motion } from "framer-motion";
import { useOnClickOutside } from "hooks/useOutsideClick";
import { toast } from "react-hot-toast";

const ALLOWED_TYPES = new Set([
  "shopify",
  "ebay",
  "amazon",
  "etsy",
  "tiktok",
  "shipstation",
  "temu",
  "yumbles",
  "opencart",
  "woocommerce",
]);

const DISPLAY_NAME: Record<string, string> = {
  amazon: "Amazon",
  shopify: "Shopify",
  ebay: "eBay",
  etsy: "Etsy",
  tiktok: "TikTok",
  shipstation: "ShipStation",
  temu: "Temu",
  yumbles: "Yumbles",
  opencart: "OpenCart",
  woocommerce: "WooCommerce",
};

const AUTO_PULL_LABEL: Record<string, string> = {
  amazon: "Auto-pulls orders every 25 min",
  shopify: "Auto-pulls orders every 10 min",
  ebay: "Auto-pulls orders every 2.5 min",
  etsy: "Auto-pulls orders every 25 min",
  tiktok: "Auto-pulls orders every 3 min",
  shipstation: "Auto-pulls orders every 5 min",
  temu: "Auto-pulls orders every 5 min",
  yumbles: "Auto-pulls orders every 5 min",
  opencart: "Auto-pulls orders every 25 min",
  woocommerce: "Auto-pulls orders every 25 min",
};

// These services answer straight away and pull the last 7 days in the
// background, so the orders appear a few minutes after the toast.
const BACKGROUND_PULL_TYPES = new Set(["shipstation", "temu"]);

type PullState = "idle" | "loading" | "success" | "error";

interface StoreStatus {
  state: PullState;
  syncedAt: Date | null;
}

function formatTimeSince(date: Date): string {
  const mins = Math.floor((Date.now() - date.getTime()) / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  return `${Math.floor(mins / 60)}h ago`;
}

export const NavigationSyncDropdown = () => {
  const dispatch = useDispatch();
  const [isOpen, setIsOpen] = useState(false);
  const [storeStatus, setStoreStatus] = useState<Record<string, StoreStatus>>(
    {},
  );

  const { salesAccountUUID, salesAccountID } = useSelector(
    (state: RootState) => state.authSlice,
  );
  const allIntegrationStores = useSelector(
    (state: RootState) => state.shipmentsSlice.allIntegrationStores,
  );

  const theme = useTheme();
  const containerRef = useRef<HTMLDivElement>(null);
  const popoverRef = useRef<HTMLElement | null>(null);

  const [triggerEl, setTriggerEl] = useState<HTMLElement | null>(null);
  const [popoverEl, setPopoverEl] = useState<HTMLElement | null>(null);

  const setPopoverElAndRef = useCallback((el: HTMLElement | null) => {
    setPopoverEl(el);
    popoverRef.current = el;
  }, []);

  const { styles: popperStyles } = usePopper(triggerEl, popoverEl, {
    strategy: "fixed",
    placement: "bottom-end",
    modifiers: [{ name: "offset", options: { offset: [0, 6] } }],
  });

  useEffect(() => {
    dispatch(fetchIntegrationStoresThunk());
  }, [salesAccountUUID]);

  const syncableStores = allIntegrationStores.filter(
    (s) =>
      s.id !== null &&
      s.status === "A" &&
      ALLOWED_TYPES.has(s.integrationType?.toLowerCase() ?? ""),
  );

  const handleButtonClick = useCallback(() => {
    setIsOpen((o) => !o);
  }, []);

  const handlePull = useCallback(
    async (storeId: string, integrationType: string | null) => {
      setStoreStatus((prev) => ({
        ...prev,
        [storeId]: {
          state: "loading",
          syncedAt: prev[storeId]?.syncedAt ?? null,
        },
      }));

      try {
        let error = false;
        let message: string | undefined;

        if (integrationType === "shipstation") {
          const result = await triggerShipstationManualPull(
            salesAccountUUID,
            salesAccountID as number,
          );
          error = result.error;
          message = result.message;
        } else {
          const result = await fetchOrders(salesAccountUUID, storeId);
          error = result.error;
        }

        if (error) {
          toast.error(message || "Failed to pull orders");
          setStoreStatus((prev) => ({
            ...prev,
            [storeId]: { state: "error", syncedAt: new Date() },
          }));
        } else {
          toast.success(
            BACKGROUND_PULL_TYPES.has(integrationType ?? "")
              ? "Manual pull started (last 7 days)"
              : "Orders pulled successfully",
          );
          setStoreStatus((prev) => ({
            ...prev,
            [storeId]: { state: "success", syncedAt: new Date() },
          }));
          dispatch(fetchShipmentsThunk());
        }
      } catch {
        toast.error("Failed to pull orders");
        setStoreStatus((prev) => ({
          ...prev,
          [storeId]: { state: "error", syncedAt: new Date() },
        }));
      }
    },
    [salesAccountUUID, salesAccountID],
  );

  useOnClickOutside([containerRef, popoverRef], () => setIsOpen(false));

  if (syncableStores.length === 0) return null;

  return (
    <div ref={containerRef} className="relative">
      {/* Trigger button */}
      <div ref={setTriggerEl} style={{ height: 32 }}>
        <Button
          size="sm"
          variant="outline"
          colorScheme="default"
          onClick={handleButtonClick}
          className="!h-full !px-3"
        >
          <span className="flex items-center gap-1.5" style={{ fontSize: 12 }}>
            <FaSyncAlt size={10} />
            Stores
            <FaChevronDown size={9} />
          </span>
        </Button>
      </div>

      {/* Stores dropdown */}
      <Portal>
        <AnimatePresence>
          {isOpen && (
            <div
              ref={setPopoverElAndRef}
              style={{ ...popperStyles.popper, zIndex: 50 }}
            >
              <motion.div
                initial={{ opacity: 0, y: -4 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -4 }}
                transition={{ duration: 0.15 }}
                style={{
                  width: 400,
                  borderRadius: theme.borderRadius,
                  backgroundColor: theme.dropdownBackgroundColor,
                  borderColor: theme.dropdownBorderColor,
                  boxShadow: theme.shadows["2xl"],
                }}
                className="border overflow-hidden"
              >
                {/* Header */}
                <div className="flex items-start justify-between px-4 pt-4 pb-3">
                  <div>
                    <div className="font-semibold text-sm">Your active stores</div>
                  </div>
                  <button
                    onClick={() => setIsOpen(false)}
                    className="p-1 rounded ml-3"
                    style={{ color: theme.colors.gray[9] }}
                  >
                    <FaTimes size={12} />
                  </button>
                </div>

                <div style={{ borderTop: `1px solid ${theme.borderColor}` }} />

                {/* Scrollable store list */}
                <div
                  className="py-1 overflow-y-auto"
                  style={{ maxHeight: "42vh" }}
                >
                  {syncableStores.map((store) => {
                    const status: StoreStatus = storeStatus[store.id!] ?? {
                      state: "idle",
                      syncedAt: null,
                    };
                    const isLoading = status.state === "loading";
                    const isError = status.state === "error";
                    const slug = store.integrationType?.toLowerCase() ?? "";
                    const integrationName = DISPLAY_NAME[slug] ?? slug;
                    const autoPullLabel = AUTO_PULL_LABEL[slug];

                    const subText = isLoading
                      ? "Pulling now…"
                      : isError && status.syncedAt
                      ? `Last pull failed · ${formatTimeSince(status.syncedAt)}`
                      : status.syncedAt
                      ? `Last pulled ${formatTimeSince(status.syncedAt)}`
                      : autoPullLabel;

                    return (
                      <div
                        key={store.id}
                        className="flex items-center px-4 py-3 gap-3 transition-colors"
                        style={{
                          cursor: "default",
                          backgroundColor: isLoading
                            ? theme.colors.gray[3]
                            : undefined,
                        }}
                        onMouseEnter={(e) => {
                          if (!isLoading)
                            (
                              e.currentTarget as HTMLDivElement
                            ).style.backgroundColor = theme.colors.gray[3];
                        }}
                        onMouseLeave={(e) => {
                          if (!isLoading)
                            (
                              e.currentTarget as HTMLDivElement
                            ).style.backgroundColor = "transparent";
                        }}
                      >
                        <Logo
                          imageUrl={`/images/logos/${slug}.png`}
                          size="xs"
                          className="flex-shrink-0"
                        />

                        <div className="flex-1 min-w-0">
                          <div
                            className="font-semibold text-sm leading-snug truncate"
                            style={{ paddingBottom: 1 }}
                          >
                            {store.displayName || store.identifier}
                          </div>
                          <div
                            className="text-xs mt-0.5"
                            style={{ color: theme.colors.gray[10] }}
                          >
                            {integrationName}
                          </div>
                          {subText && (
                            <div
                              className="text-xs mt-0.5"
                              style={{
                                color: isError
                                  ? theme.colors.orange[9]
                                  : theme.colors.gray[10],
                              }}
                            >
                              {subText}
                            </div>
                          )}
                        </div>

                        {isLoading ? (
                          <span
                            className="flex items-center gap-1.5 text-xs font-semibold flex-shrink-0"
                            style={{ color: theme.colors.blue[9] }}
                          >
                            <span
                              className="inline-block w-2 h-2 rounded-full"
                              style={{ backgroundColor: theme.colors.blue[9] }}
                            />
                            Pulling
                          </span>
                        ) : (
                          <button
                            onClick={() =>
                              handlePull(store.id!, store.integrationType)
                            }
                            className="flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-md flex-shrink-0 border"
                            style={{
                              borderColor: isError
                                ? theme.colors.orange[7]
                                : theme.borderColor,
                              color: isError
                                ? theme.colors.orange[9]
                                : theme.textColorPrimary,
                              backgroundColor: isError
                                ? theme.colors.orange[3]
                                : "transparent",
                            }}
                            onMouseEnter={(e) => {
                              const btn = e.currentTarget as HTMLButtonElement;
                              btn.style.backgroundColor = isError
                                ? theme.colors.orange[4]
                                : theme.colors.gray[4];
                              btn.style.borderColor = isError
                                ? theme.colors.orange[8]
                                : theme.colors.gray[9];
                            }}
                            onMouseLeave={(e) => {
                              const btn = e.currentTarget as HTMLButtonElement;
                              btn.style.backgroundColor = isError
                                ? theme.colors.orange[3]
                                : "transparent";
                              btn.style.borderColor = isError
                                ? theme.colors.orange[7]
                                : theme.borderColor;
                            }}
                          >
                            <FaSyncAlt size={10} />
                            {isError ? "Retry" : "Pull Now"}
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              </motion.div>
            </div>
          )}
        </AnimatePresence>
      </Portal>
    </div>
  );
};
